import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import moment from 'moment';
import format from 'string-template';

import { StyledTabs } from '../../components/ClientInfo/styles';
import CoachSelect from '../../components/Leads/components/CoachSelect';
import { HeaderRow, TitleContainer, Title } from '../../../components/v2/Header';
import InternalAssignment from '../../../Model/InternalAssignment';
import { AssignmentRole } from '../../../utils/internalAssignments';
import LoadingOverlay from '../../components/LoadingOverlay';
import Table from '../../../components/v2/Table';
import { DateFormat } from '../../../utils/date';
import Button from '../../../components/Button';
import { PrimaryButton } from '../../../components/Button/ActionButtons';
import useComponentMounted from '../../../hooks/useComponentMounted';
import ConfirmationModal from '../../../components/v2/ConfirmationModal/ConfirmationModal';
import { TableClasses } from '../../../components/v2/Table/styles';
import CoachHistory, { HistoryEventType } from '../../Model/CoachHistory';
import LoggedInUserContext from '../../../context/LoggedInUserContext';
import CoachesListContext from '../../context/CoachesListContext';
import useToast from '../../hooks/useToast';
import AddAssignmentModal from './AddAssignmentModal';
import PoolAssignmentCard from './PoolAssignmentCard';
import PoolRosterModal from './PoolRosterModal';
import RemoveFromPoolModal from './RemoveFromPoolModal';
import MoveToPoolDialog from './MoveToPoolDialog';
import useMoveToPool from './hooks/useMoveToPool';
import usePoolRoster from './hooks/usePoolRoster';
import {
  getAcDisplayName,
  getAcNamesByCoach,
  getPoolRosterRows,
  getPoolTransitions,
  isCoachPoolEligible,
} from './utils/poolAssignments';
import {
  ButtonContainer, AddLiaisonButton, PoolButton, UserNameContainer, LiaisonChip,
} from './styles';

import texts from './texts';

const InternalAssignments = () => {
  const { userId: loggedInUserId, userDoc } = useContext(LoggedInUserContext);
  const { coachesList, coachesCollection } = useContext(CoachesListContext);
  const [selectedRole, setSelectedRole] = useState(AssignmentRole.INSIDE_SALES);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [internalAssignments, setInternalAssignments] = useState({ docs: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [openAddAssignmentModal, setOpenAddAssignmentModal] = useState(false);
  const [assignmentToRemove, setAssignmentToRemove] = useState(null);
  const [removalNote, setRemovalNote] = useState('');
  const [isLiaisonMode, setIsLiaisonMode] = useState(false);
  const [isRosterOpen, setIsRosterOpen] = useState(false);
  const [coachToRemove, setCoachToRemove] = useState(null);
  const [isRemovalBlockedByPods, setIsRemovalBlockedByPods] = useState(false);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const {
    startMoveToPool,
    confirmMoveToPool,
    cancelMoveToPool,
    transitionsToConfirm,
    isSaving,
  } = useMoveToPool();

  const isAcTab = selectedRole === AssignmentRole.ASSISTANT_COACH;
  const isLastAcAssignment = isAcTab && internalAssignments.docs.length === 1;
  const isSelectedCoachPoolEligible = !!selectedCoach
    && isCoachPoolEligible(coachesCollection.docs.slice(), selectedCoach.id);

  const {
    poolAssignmentsCol,
    acAssignmentsCol,
    isLoading: isPoolLoading,
    loadError: poolLoadError,
    reload: reloadPool,
  } = usePoolRoster(isAcTab);

  const acNamesByCoach = getAcNamesByCoach(acAssignmentsCol.docs.slice());
  const poolAssignments = poolAssignmentsCol.docs.slice();
  const rosterRows = getPoolRosterRows(poolAssignments, coachesList);
  const isCoachPooled = !!selectedCoach && poolAssignments.some(({ coach }) => coach === selectedCoach.id);
  const currentAcNames = (selectedCoach && acNamesByCoach[selectedCoach.id]) || [];
  const arePoolActionsDisabled = isPoolLoading || !!poolLoadError || isSaving;

  const moveToPool = (coaches) => startMoveToPool(getPoolTransitions(coaches, acNamesByCoach));

  useEffect(() => {
    const fetchInternalAssignments = async () => {
      if (selectedCoach?.id) {
        setIsLoading(true);
        const assignments = await InternalAssignment.getAllCurrentAssignments(null, selectedRole, selectedCoach.id);
        if (isComponentMountedRef.current) {
          setInternalAssignments(assignments);
          setIsLoading(false);
        }
      } else {
        setInternalAssignments({ docs: [] });
      }
    };
    fetchInternalAssignments();
  }, [isComponentMountedRef, selectedCoach, selectedRole]);

  const onRemoveAssignment = async (assignment) => {
    setIsLoading(true);
    try {
      await assignment.disableAssignment();

      if (assignment.role === AssignmentRole.CAM) {
        await CoachHistory.addCoachHistoryItem({
          type: HistoryEventType.CAM_ASSIGNMENT_UPDATED,
          coachId: assignment.coach,
          actionedBy: loggedInUserId,
          actionedByUserName: userDoc?.name,
          metadata: {
            previousValue: {
              userId: assignment.userId,
              userName: assignment.userName,
            },
            newValue: '',
            ...(removalNote && { note: removalNote }),
          },
        });
      }
    } catch (error) {
      showToast(format(texts.assignmentRemovalFailed, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        setRemovalNote('');
        setAssignmentToRemove(null);
        setIsRemovalBlockedByPods(false);
      }
    }
  };

  const onRemoveAssignmentClick = (assignment) => {
    // A coach without Assistant Coaching can't be pooled, so their last AC is just removed.
    if (isLastAcAssignment && isSelectedCoachPoolEligible) {
      startMoveToPool(
        [{
          id: selectedCoach.id,
          name: selectedCoach.name,
          currentAcNames: [getAcDisplayName(assignment)],
        }],
        {
          onBlockedByPods: () => {
            setIsRemovalBlockedByPods(true);
            setAssignmentToRemove(assignment);
          },
        },
      );
      return;
    }

    setAssignmentToRemove(assignment);
  };

  const handleCloseRemovalModal = useCallback(() => {
    setAssignmentToRemove(null);
    setRemovalNote('');
    setIsRemovalBlockedByPods(false);
  }, []);

  useEffect(() => {
    if (poolLoadError) {
      showToast(format(texts.poolLoadFailed, { error: poolLoadError.message }), { error: true });
    }
  }, [poolLoadError, showToast]);

  const onCoachRemovedFromPool = useCallback(() => {
    showToast(format(texts.coachRemovedFromPool, { coach: coachToRemove.name }), { success: true });
  }, [coachToRemove, showToast]);

  const openModal = (isLiaison) => {
    setIsLiaisonMode(isLiaison);
    setOpenAddAssignmentModal(true);
  };

  return (
    <>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.internalAssignments}</Title>
        </TitleContainer>
      </HeaderRow>
      <HeaderRow>
        <CoachSelect
          onChange={(selection) => {
            if (!selection) {
              setSelectedCoach(null);
              return;
            }
            const { id: coachId, label: coachName } = selection;
            setSelectedCoach({ id: coachId, name: coachName });
          }}
        />
      </HeaderRow>
      <StyledTabs
        tabsConfig={{
          [AssignmentRole.INSIDE_SALES]: { label: texts.tabLabels[AssignmentRole.INSIDE_SALES] },
          [AssignmentRole.ASSISTANT_COACH]: { label: texts.tabLabels[AssignmentRole.ASSISTANT_COACH] },
          [AssignmentRole.CAM]: { label: texts.tabLabels[AssignmentRole.CAM] },
          [AssignmentRole.CPS]: { label: texts.tabLabels[AssignmentRole.CPS] },
        }}
        selectedTab={selectedRole}
        onSelectTab={setSelectedRole}
      />
      <LoadingOverlay isLoading={isLoading} />
      {isAcTab && !!selectedCoach && !isPoolLoading && !poolLoadError && (
        <PoolAssignmentCard
          coachName={selectedCoach.name}
          isCoachPooled={isCoachPooled}
          currentAcNames={currentAcNames}
          isCoachPoolEligible={isSelectedCoachPoolEligible}
        />
      )}
      <ButtonContainer>
        {isAcTab && (
          <>
            {/*
              A failed pool load disables every pool control, including removing a coach's last AC,
              so the recovery has to be reachable from the same row rather than by knowing that
              flipping tabs refetches.
            */}
            {poolLoadError ? (
              <PoolButton
                full={false}
                onClick={reloadPool}
                disabled={isPoolLoading}
              >
                {texts.retryPoolLoad}
              </PoolButton>
            ) : (
              <PoolButton
                full={false}
                onClick={() => setIsRosterOpen(true)}
                disabled={isPoolLoading}
              >
                {format(texts.viewPool, { count: rosterRows.length })}
              </PoolButton>
            )}
            {!!selectedCoach && (isCoachPooled ? (
              <PoolButton
                full={false}
                onClick={() => setCoachToRemove(selectedCoach)}
                disabled={arePoolActionsDisabled}
              >
                {texts.removeFromPool}
              </PoolButton>
            ) : (
              <PoolButton
                full={false}
                onClick={() => moveToPool([selectedCoach])}
                disabled={arePoolActionsDisabled}
              >
                {texts.moveToPool}
              </PoolButton>
            ))}
            <AddLiaisonButton
              full={false}
              onClick={() => openModal(true)}
              disabled={!selectedCoach}
            >
              {texts.addLiaison}
            </AddLiaisonButton>
          </>
        )}
        <Button
          full={false}
          onClick={() => openModal(false)}
          disabled={!selectedCoach}
        >
          {texts.addAssignment}
        </Button>
      </ButtonContainer>
      <Table
        rows={internalAssignments.docs.slice()}
        columns={[
          {
            field: 'userName',
            headerName: texts.tableColumns.user,
            quickSearch: true,
            flex: 1,
            renderCell: ({ row }) => (
              <UserNameContainer>
                {row.userName}
                {row.isLiaison && (
                <LiaisonChip
                  label="Liaison"
                  size="small"
                />
                )}
              </UserNameContainer>
            ),
          },
          {
            field: 'createdAt',
            headerName: texts.tableColumns.createdAt,
            renderCell: ({ row }) => (
              <div>{moment(row.createdAt).format(DateFormat.DEFAULT_DATE_FORMAT)}</div>
            ),
            flex: 1,
          },
          {
            field: 'userId',
            headerName: texts.tableColumns.userId,
            flex: 1,
          },
          {
            field: 'actions',
            headerName: texts.tableColumns.actions,
            flex: 1,
            /*
              Removing the last AC is the one path in this shared table that writes to the pool, so
              it alone waits on the pool having loaded. The button cannot gate on
              arePoolActionsDisabled outright: usePoolRoster only clears isLoading on the AC tab, so
              that flag stays true for the whole life of the IS, CAM and CPS tabs.
            */
            renderCell: ({ row }) => (
              <PrimaryButton
                onClick={() => onRemoveAssignmentClick(row)}
                variant="info"
                size="medium"
                disabled={isSaving || (isLastAcAssignment && arePoolActionsDisabled)}
              >
                {texts.removeAssignment}
              </PrimaryButton>
            ),
          },
        ]}
        isLoading={isLoading}
        getRowClassName={(params) => (params.row.isLiaison ? TableClasses.HIGHLIGHTED_ROW : '')}
        disableRowSelectionOnClick
      />
      {openAddAssignmentModal && (
        <AddAssignmentModal
          role={selectedRole}
          coachName={selectedCoach?.name}
          coachId={selectedCoach?.id}
          isOpen={openAddAssignmentModal}
          onClose={() => setOpenAddAssignmentModal(false)}
          internalAssignments={internalAssignments.docs}
          isLiaison={isLiaisonMode}
        />
      )}
      {assignmentToRemove && (
        <ConfirmationModal
          open={!!assignmentToRemove}
          onClose={handleCloseRemovalModal}
          onConfirm={() => onRemoveAssignment(assignmentToRemove)}
          title={texts.removeAssignmentTitle}
          description={isRemovalBlockedByPods
            ? format(texts.podBlockedRemovalDescription, { user: assignmentToRemove.userName })
            : assignmentToRemove.userName}
          showAdditionalInput={assignmentToRemove.role === AssignmentRole.CAM}
          onInputChange={setRemovalNote}
        />
      )}
      {transitionsToConfirm.length > 0 && (
        <MoveToPoolDialog
          transitions={transitionsToConfirm}
          onCancel={cancelMoveToPool}
          onConfirm={confirmMoveToPool}
          isSaving={isSaving}
        />
      )}
      {!!coachToRemove && (
        <RemoveFromPoolModal
          coach={coachToRemove}
          onClose={() => setCoachToRemove(null)}
          onRemoved={onCoachRemovedFromPool}
        />
      )}
      {isRosterOpen && (
        <PoolRosterModal
          onClose={() => setIsRosterOpen(false)}
          rosterRows={rosterRows}
          onAddCoachesToPool={moveToPool}
          onRemoveCoach={setCoachToRemove}
          isSaving={isSaving}
        />
      )}
    </>
  );
};

export default compose(
  observer,
)(InternalAssignments);
